// Offline write outbox — generic single-role model (2026-05-29).
// Same core pattern as v1 (IndexedDB queue + blobs, FIFO replay, network-
// error fallback, client UUIDs preserving FK integrity) but with simpler ops
// matching the new schema (no per-trade rows; one status per snag).
//
// Op coverage:
//   save_snag, set_status, set_assignee, set_due_date,
//   mark_done (with completion photos), kick_back, close_snag,
//   edit_snag, delete_snag, add_media, delete_media, save_annotation

import { openDB, type IDBPDatabase } from 'idb'
import { supabase } from './supabase'
import { compressForUpload } from './photoCompress'
import type { SnagStatus, SnagPriority } from './types'

const DB_NAME = 'signed-and-sealed-offline'
const DB_VERSION = 1
const STORE_QUEUE = 'queue'
const STORE_BLOBS = 'blobs'

type OpKind =
  | 'save_snag' | 'set_status' | 'set_assignee' | 'set_due_date'
  | 'mark_done' | 'kick_back' | 'close_snag'
  | 'edit_snag' | 'delete_snag'
  | 'add_media' | 'delete_media' | 'save_annotation'
  | 'save_project'

interface QueueEntry<P = unknown> {
  id: string; kind: OpKind; payload: P; tenant_id: string
  createdAt: number; attempts: number; lastError?: string | null
}

interface MediaItemQueueable {
  id: string; blobKey: string; path: string
  type: 'photo' | 'video'; contentType: string
}
interface MediaItem extends MediaItemQueueable { file: File }

// ----- DB plumbing -----
let dbPromise: Promise<IDBPDatabase> | null = null
function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_QUEUE)) db.createObjectStore(STORE_QUEUE, { keyPath: 'id' })
        if (!db.objectStoreNames.contains(STORE_BLOBS)) db.createObjectStore(STORE_BLOBS)
      },
    })
  }
  return dbPromise
}
function uuid() { return crypto.randomUUID() }

// ----- Subscriptions -----
const pendingListeners = new Set<(n: number) => void>()
const drainListeners = new Set<(d: boolean) => void>()
let cachedPending = 0
let draining = false

export function subscribePending(fn: (n: number) => void) {
  pendingListeners.add(fn); fn(cachedPending); return () => pendingListeners.delete(fn)
}
export function subscribeDraining(fn: (d: boolean) => void) {
  drainListeners.add(fn); fn(draining); return () => drainListeners.delete(fn)
}
async function refreshPending() {
  const db = await getDB(); cachedPending = await db.count(STORE_QUEUE)
  pendingListeners.forEach(fn => fn(cachedPending)); return cachedPending
}
function setDraining(v: boolean) { draining = v; drainListeners.forEach(fn => fn(draining)) }

// ----- Blob + queue helpers -----
async function putBlob(key: string, blob: Blob) { (await getDB()).put(STORE_BLOBS, blob, key) }
async function getBlob(key: string) { return (await getDB()).get(STORE_BLOBS, key) as Promise<Blob | undefined> }
async function deleteBlob(key: string) { (await getDB()).delete(STORE_BLOBS, key) }

async function enqueue<P>(kind: OpKind, payload: P, tenant_id: string) {
  const db = await getDB()
  await db.put(STORE_QUEUE, { id: uuid(), kind, payload, tenant_id, createdAt: Date.now(), attempts: 0, lastError: null })
  refreshPending(); drainQueue().catch(() => {})
}
async function listQueue(): Promise<QueueEntry[]> {
  const db = await getDB()
  const all = (await db.getAll(STORE_QUEUE)) as QueueEntry[]
  all.sort((a, b) => a.createdAt - b.createdAt)
  return all
}
async function deleteQueueEntry(id: string) { (await getDB()).delete(STORE_QUEUE, id) }
async function recordError(id: string, message: string) {
  const db = await getDB()
  const e = (await db.get(STORE_QUEUE, id)) as QueueEntry | undefined
  if (!e) return
  e.lastError = message; e.attempts = (e.attempts || 0) + 1
  await db.put(STORE_QUEUE, e)
}
function isNetworkError(err: unknown) {
  if (!err) return false
  const message = (err as { message?: string }).message ?? String(err)
  if (/Failed to fetch|NetworkError|TypeError|fetch|ECONN/i.test(message)) return true
  if ((err as { name?: string }).name === 'TypeError') return true
  return false
}

async function queueOrRun<P>(
  kind: OpKind, payload: P, tenantId: string, fn: () => Promise<void>,
): Promise<{ queued: boolean }> {
  if (navigator.onLine) {
    try { await fn(); return { queued: false } }
    catch (e) { if (!isNetworkError(e)) throw e }
  }
  await enqueue(kind, payload, tenantId)
  return { queued: true }
}
async function simpleUpdate(table: string, id: string, patch: Record<string, unknown>) {
  const { error } = await supabase.from(table).update(patch).eq('id', id)
  if (error) throw error
}

// ============================================================================
// Public API
// ============================================================================

interface SaveSnagInput {
  tenantId: string
  projectId: string
  title: string
  location: string | null
  description: string | null
  priority: SnagPriority
  dueDate: string | null
  assigneeContactId: string | null
  assigneeName: string | null
  assigneeEmail: string | null
  assigneePhone: string | null
  createdByUserId: string | null
  tagIds: string[]
  photos: File[]
  pinX?: number | null
  pinY?: number | null
}

export async function saveSnag(input: SaveSnagInput): Promise<{ snagId: string; queued: boolean }> {
  const snagId = uuid()
  const snagRow = {
    id: snagId, tenant_id: input.tenantId, project_id: input.projectId,
    title: input.title, location: input.location, description: input.description,
    priority: input.priority, status: 'open' as SnagStatus, due_date: input.dueDate,
    assignee_contact_id: input.assigneeContactId,
    assignee_name: input.assigneeName, assignee_email: input.assigneeEmail, assignee_phone: input.assigneePhone,
    created_by_user_id: input.createdByUserId,
    pin_x: input.pinX ?? null,
    pin_y: input.pinY ?? null,
  }
  const snagTagRows = input.tagIds.map(tagId => ({ tenant_id: input.tenantId, snag_id: snagId, tag_id: tagId }))

  const compressed = await Promise.all(input.photos.map(compressForUpload))
  const mediaItems: MediaItem[] = compressed.map(file => {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
    return {
      id: uuid(), blobKey: `blob_${uuid()}`, file,
      path: `${input.tenantId}/${snagId}/initial_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`,
      type: file.type.startsWith('video') ? 'video' : 'photo',
      contentType: file.type || 'image/jpeg',
    }
  })

  if (navigator.onLine) {
    try {
      // Cap online attempt at 6 s. If Supabase is cold, the bare INSERT
      // can take 20-30 s on free tier — the user shouldn't sit on a
      // "Saving…" spinner that long. Race the insert against a timeout;
      // if it loses, fall through to the offline outbox which will drain
      // it as soon as the connection resolves. Either way the user sees
      // navigation happen immediately.
      const inlineRun = runSaveSnag({ snagRow, snagTagRows, mediaItems, tenantId: input.tenantId })
      const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('save online timeout — queuing')), 6000))
      await Promise.race([inlineRun, timeout])
      return { snagId, queued: false }
    } catch (e) {
      const msg = (e as { message?: string })?.message || ''
      // Network blip OR our own timeout → queue. Anything else (constraint
      // violation, RLS, etc.) is a real error that the caller needs to see.
      if (!isNetworkError(e) && !msg.includes('save online timeout')) throw e
    }
  }
  for (const m of mediaItems) await putBlob(m.blobKey, m.file)
  await enqueue('save_snag', {
    snagRow, snagTagRows,
    mediaItems: mediaItems.map(({ file, ...rest }) => rest),
    tenantId: input.tenantId,
  }, input.tenantId)
  return { snagId, queued: true }
}

// Project create — same optimistic / outbox-fallback pattern as saveSnag.
// Until 2026-05-31 this was a bare awaited INSERT in Projects.tsx, which
// meant a cold-Supabase round trip could pin the modal on "Saving…" for
// 60s+. Now: client-side UUID, 5s race against an online insert, fall
// through to the outbox if the network is slow. Either way the modal
// closes immediately and the user sees the new project right away (the
// caller does an optimistic insert into the React Query cache).
interface SaveProjectInput {
  tenantId: string
  name: string
  clientName: string | null
  createdByUserId: string | null
}
export async function saveProject(input: SaveProjectInput): Promise<{ projectId: string; queued: boolean }> {
  const projectId = uuid()
  const projectRow = {
    id: projectId,
    tenant_id: input.tenantId,
    name: input.name,
    client_name: input.clientName,
    created_by_user_id: input.createdByUserId,
  }

  if (navigator.onLine) {
    try {
      const inlineRun = (async () => {
        const { error } = await supabase.from('projects').insert(projectRow)
        if (error) throw error
      })()
      // 5s cap. Cold Supabase free tier can take 20-30s on its first hit
      // after sleeping; we want the modal closed long before then.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('project save online timeout — queuing')), 5000))
      await Promise.race([inlineRun, timeout])
      return { projectId, queued: false }
    } catch (e) {
      const msg = (e as { message?: string })?.message || ''
      // Only swallow network blips / our own timeout. Real errors (RLS,
      // constraint violations) must bubble up so the caller can show them.
      if (!isNetworkError(e) && !msg.includes('project save online timeout')) throw e
    }
  }
  await enqueue('save_project', { projectRow, tenantId: input.tenantId }, input.tenantId)
  return { projectId, queued: true }
}

export async function setStatus(tenantId: string, snagId: string, status: 'open' | 'in_progress') {
  return queueOrRun('set_status', { snagId, status }, tenantId, async () => {
    await simpleUpdate('snags', snagId, { status, kick_back_note: null })
  })
}

export async function setAssignee(tenantId: string, snagId: string, patch: {
  assignee_contact_id?: string | null
  assignee_name?: string | null
  assignee_email?: string | null
  assignee_phone?: string | null
}) {
  return queueOrRun('set_assignee', { snagId, patch }, tenantId, () => simpleUpdate('snags', snagId, patch))
}

export async function setDueDate(tenantId: string, snagId: string, dueDate: string | null) {
  return queueOrRun('set_due_date', { snagId, dueDate }, tenantId, () => simpleUpdate('snags', snagId, { due_date: dueDate }))
}

interface MarkDoneInput {
  tenantId: string
  snagId: string
  markedByUserId: string | null
  completionFiles: File[]
}
export async function markDone(input: MarkDoneInput): Promise<{ queued: boolean }> {
  const compressed = await Promise.all(input.completionFiles.map(compressForUpload))
  const mediaItems: MediaItem[] = compressed.map((file, i) => {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
    return {
      id: uuid(), blobKey: `blob_${uuid()}`, file,
      path: `${input.tenantId}/${input.snagId}/completion_${Date.now()}_${i}.${ext}`,
      type: file.type.startsWith('video') ? 'video' : 'photo',
      contentType: file.type || 'image/jpeg',
    }
  })
  if (navigator.onLine) {
    try {
      await runMarkDone({ ...input, mediaItems })
      return { queued: false }
    } catch (e) { if (!isNetworkError(e)) throw e }
  }
  for (const m of mediaItems) await putBlob(m.blobKey, m.file)
  await enqueue('mark_done', {
    snagId: input.snagId, markedByUserId: input.markedByUserId,
    mediaItems: mediaItems.map(({ file, ...rest }) => rest),
    tenantId: input.tenantId,
  }, input.tenantId)
  return { queued: true }
}

export async function kickBack(tenantId: string, snagId: string, note: string) {
  return queueOrRun('kick_back', { snagId, note }, tenantId, async () => {
    await simpleUpdate('snags', snagId, { status: 'in_progress', kick_back_note: note })
  })
}

export async function closeSnag(tenantId: string, snagId: string, closedByUserId: string) {
  return queueOrRun('close_snag', { snagId, closedByUserId }, tenantId, async () => {
    await simpleUpdate('snags', snagId, {
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_by_user_id: closedByUserId,
      kick_back_note: null,
    })
  })
}

export async function editSnag(tenantId: string, snagId: string, patch: {
  title?: string; location?: string | null; description?: string | null; priority?: SnagPriority
}) {
  return queueOrRun('edit_snag', { snagId, patch }, tenantId, () => simpleUpdate('snags', snagId, patch))
}

export async function reopenSnag(tenantId: string, snagId: string, note: string | null) {
  return queueOrRun('reopen_snag' as any, { snagId, note }, tenantId, async () => {
    await simpleUpdate('snags', snagId, {
      status: 'open',
      closed_at: null,
      closed_by_user_id: null,
      marked_done_at: null,
      marked_done_by_user_id: null,
      kick_back_note: note ? `Reopened: ${note}` : null,
    })
  })
}

export async function deleteSnag(tenantId: string, snagId: string) {
  return queueOrRun('delete_snag', { snagId }, tenantId, async () => {
    const { error } = await supabase.from('snags').delete().eq('id', snagId)
    if (error) throw error
  })
}

interface AddMediaInput {
  tenantId: string; snagId: string; files: File[]; uploadedByUserId: string | null
}
export async function addMedia(input: AddMediaInput) {
  const compressed = await Promise.all(input.files.map(compressForUpload))
  const items: MediaItem[] = compressed.map(file => {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
    return {
      id: uuid(), blobKey: `blob_${uuid()}`, file,
      path: `${input.tenantId}/${input.snagId}/initial_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`,
      type: file.type.startsWith('video') ? 'video' : 'photo',
      contentType: file.type || 'image/jpeg',
    }
  })
  if (navigator.onLine) {
    try { await runAddMedia({ items, ...input }); return { queued: false } }
    catch (e) { if (!isNetworkError(e)) throw e }
  }
  for (const m of items) await putBlob(m.blobKey, m.file)
  await enqueue('add_media', {
    items: items.map(({ file, ...rest }) => rest),
    snagId: input.snagId, uploadedByUserId: input.uploadedByUserId, tenantId: input.tenantId,
  }, input.tenantId)
  return { queued: true }
}

export async function deleteMedia(tenantId: string, mediaId: string, storagePath: string) {
  return queueOrRun('delete_media', { mediaId, storagePath }, tenantId, async () => {
    await supabase.storage.from('snag-media').remove([storagePath]).catch(() => {})
    const { error } = await supabase.from('snag_media').delete().eq('id', mediaId)
    if (error) throw error
  })
}

interface SaveAnnotationInput {
  tenantId: string; snagId: string; parentMediaId: string
  previousAnnotation?: { id: string; storage_path: string } | null
  blob: Blob; uploadedByUserId: string | null
}
export async function saveAnnotation(input: SaveAnnotationInput) {
  const path = `${input.tenantId}/${input.snagId}/annotated_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`
  const blobKey = `blob_${uuid()}`
  if (navigator.onLine) {
    try { await runSaveAnnotation({ ...input, path }); return { queued: false } }
    catch (e) { if (!isNetworkError(e)) throw e }
  }
  await putBlob(blobKey, input.blob)
  await enqueue('save_annotation', { ...input, blob: undefined, path, blobKey }, input.tenantId)
  return { queued: true }
}

// ----- Online runners -----

// Upload one media item: storage put → DB insert → drop the local blob.
// Pulled out so each runner can do `Promise.all` over the media set instead
// of waiting in series. With 3 photos the user goes from ~3 × 5 s ≈ 15 s
// to ~5 s, since the three round-trips overlap.
async function uploadOneMedia(args: {
  m: MediaItem | MediaItemQueueable
  tenantId: string
  snagId: string
  kind: 'initial' | 'completion' | 'annotated'
  uploadedByUserId?: string | null
  parentMediaId?: string | null
}): Promise<{ mediaId: string | null }> {
  const { m, tenantId, snagId, kind, uploadedByUserId, parentMediaId } = args
  const blob = ('file' in m && m.file) ? m.file : await getBlob(m.blobKey)
  if (!blob) return { mediaId: null }
  const { error: upErr } = await supabase.storage.from('snag-media').upload(m.path, blob, { contentType: m.contentType })
  if (upErr) throw upErr
  const row: Record<string, unknown> = {
    id: m.id, tenant_id: tenantId, snag_id: snagId,
    type: m.type, storage_path: m.path, kind,
  }
  if (uploadedByUserId !== undefined) row.uploaded_by_user_id = uploadedByUserId
  if (parentMediaId !== undefined) row.parent_media_id = parentMediaId
  const { error: insErr } = await supabase.from('snag_media').insert(row)
  if (insErr) throw insErr
  if (m.blobKey) await deleteBlob(m.blobKey)
  return { mediaId: m.id }
}

async function runSaveSnag(args: {
  snagRow: Record<string, unknown>
  snagTagRows: Record<string, unknown>[]
  mediaItems: MediaItem[] | MediaItemQueueable[]
  tenantId: string
}) {
  // Snag row first (rest of inserts FK to it).
  const { error: snagErr } = await supabase.from('snags').insert(args.snagRow)
  if (snagErr) throw snagErr
  // Tags + media uploads can all run in parallel from here. Tags are cheap;
  // photos are the slow part and overlapping them is the headline win.
  const snagId = (args.snagRow as { id: string }).id
  await Promise.all([
    args.snagTagRows.length
      ? supabase.from('snag_tags').insert(args.snagTagRows).then(({ error }) => { if (error) throw error })
      : Promise.resolve(),
    ...args.mediaItems.map(m => uploadOneMedia({ m, tenantId: args.tenantId, snagId, kind: 'initial' })),
  ])
}

async function runMarkDone(args: {
  snagId: string; markedByUserId: string | null
  mediaItems: MediaItem[] | MediaItemQueueable[]; tenantId: string
}) {
  // Photos in parallel, then the status flip once they've all landed.
  await Promise.all(args.mediaItems.map(m =>
    uploadOneMedia({ m, tenantId: args.tenantId, snagId: args.snagId, kind: 'completion' })
  ))
  await simpleUpdate('snags', args.snagId, {
    status: 'done',
    marked_done_at: new Date().toISOString(),
    marked_done_by_user_id: args.markedByUserId,
    kick_back_note: null,
  })
}

async function runAddMedia(args: {
  items: MediaItem[] | MediaItemQueueable[]
  snagId: string; uploadedByUserId: string | null; tenantId: string
}) {
  await Promise.all(args.items.map(m =>
    uploadOneMedia({
      m, tenantId: args.tenantId, snagId: args.snagId, kind: 'initial',
      uploadedByUserId: args.uploadedByUserId,
    })
  ))
}

async function runSaveAnnotation(args: {
  tenantId: string; snagId: string; parentMediaId: string
  previousAnnotation?: { id: string; storage_path: string } | null
  blob?: Blob; blobKey?: string; path: string; uploadedByUserId: string | null
}) {
  if (args.previousAnnotation) {
    await supabase.storage.from('snag-media').remove([args.previousAnnotation.storage_path]).catch(() => {})
    await supabase.from('snag_media').delete().eq('id', args.previousAnnotation.id)
  }
  const blob = args.blob ?? (args.blobKey ? await getBlob(args.blobKey) : undefined)
  if (!blob) throw new Error('annotation blob missing')
  const { error: upErr } = await supabase.storage.from('snag-media').upload(args.path, blob, { contentType: 'image/jpeg' })
  if (upErr) throw upErr
  const { error: insErr } = await supabase.from('snag_media').insert({
    tenant_id: args.tenantId, snag_id: args.snagId,
    type: 'photo', storage_path: args.path, kind: 'annotated',
    parent_media_id: args.parentMediaId, uploaded_by_user_id: args.uploadedByUserId,
  })
  if (insErr) throw insErr
  if (args.blobKey) await deleteBlob(args.blobKey)
}

// ----- Drain -----
export async function drainQueue() {
  if (draining || !navigator.onLine) return
  setDraining(true)
  try {
    while (true) {
      const queue = await listQueue()
      if (queue.length === 0) break
      const op = queue[0]
      try {
        switch (op.kind) {
          case 'save_snag':       await runSaveSnag(op.payload as any); break
          case 'save_project':    { const p = op.payload as any; const { error } = await supabase.from('projects').insert(p.projectRow); if (error) throw error; break }
          case 'set_status':      { const p = op.payload as any; await simpleUpdate('snags', p.snagId, { status: p.status, kick_back_note: null }); break }
          case 'set_assignee':    { const p = op.payload as any; await simpleUpdate('snags', p.snagId, p.patch); break }
          case 'set_due_date':    { const p = op.payload as any; await simpleUpdate('snags', p.snagId, { due_date: p.dueDate }); break }
          case 'mark_done':       await runMarkDone(op.payload as any); break
          case 'kick_back':       { const p = op.payload as any; await simpleUpdate('snags', p.snagId, { status: 'in_progress', kick_back_note: p.note }); break }
          case 'close_snag':      { const p = op.payload as any; await simpleUpdate('snags', p.snagId, { status: 'closed', closed_at: new Date().toISOString(), closed_by_user_id: p.closedByUserId, kick_back_note: null }); break }
          case 'edit_snag':       { const p = op.payload as any; await simpleUpdate('snags', p.snagId, p.patch); break }
          case 'delete_snag':     { const p = op.payload as any; await supabase.from('snags').delete().eq('id', p.snagId); break }
          case 'add_media':       await runAddMedia(op.payload as any); break
          case 'delete_media':    { const p = op.payload as any; await supabase.storage.from('snag-media').remove([p.storagePath]).catch(() => {}); await supabase.from('snag_media').delete().eq('id', p.mediaId); break }
          case 'save_annotation': await runSaveAnnotation(op.payload as any); break
        }
        await deleteQueueEntry(op.id); await refreshPending()
      } catch (e) {
        const msg = (e as { message?: string }).message ?? String(e)
        // IDEMPOTENT REPLAY: if the underlying row already exists (duplicate
        // PK on insert) OR the target row is gone (delete on already-deleted),
        // treat as success and move on. This handles the race where the
        // INLINE attempt actually completed on Supabase but the JS-side 6s
        // timeout fired first, leaving the same op queued. Without this the
        // op gets stuck retrying forever showing "Syncing 1…" with no way
        // to recover except clearing IndexedDB by hand.
        const idempotentOk =
          /duplicate key|already exists|23505/i.test(msg) ||                       // insert hit existing PK
          /No rows|0 rows|PGRST116/i.test(msg)                                      // update/delete hit nothing
        if (idempotentOk) {
          console.warn('[offline] op already applied server-side, dropping queue entry', op.kind, op.id)
          // Clean up any blobs the op was carrying.
          const payload = op.payload as any
          for (const m of (payload?.mediaItems || payload?.items || [])) {
            if (m?.blobKey) await deleteBlob(m.blobKey).catch(() => {})
          }
          if (payload?.blobKey) await deleteBlob(payload.blobKey).catch(() => {})
          await deleteQueueEntry(op.id); await refreshPending()
          continue
        }
        await recordError(op.id, msg)
        console.warn('[offline] sync paused at', op.id, e)
        break
      }
    }
  } finally { setDraining(false) }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { drainQueue() })
  // Also drain when the user brings the tab back to foreground — without
  // this a queued snag can sit pending until the next mutation triggers
  // a drain. visibilitychange fires on tab-switch + on iOS PWA resume.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') drainQueue().catch(() => {})
  })
  // And on window focus for desktop browsers where visibilitychange
  // doesn't always fire reliably.
  window.addEventListener('focus', () => { drainQueue().catch(() => {}) })
  setTimeout(() => { refreshPending().catch(() => {}); drainQueue().catch(() => {}) }, 500)
}
